import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Icon,
  List,
  NavigationBar,
  NavigationRail,
  SnackbarProvider,
  iconChevron,
  iconMenu,
  type NavItem,
} from '@sauda/ui/kit';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import type { TodayData } from '../lib/dashboard.js';
import { AccountSheet } from './AccountSheet.js';
import { Banners, isReadOnly, useBillingStatus } from './Banners.js';
import { ChromeContext } from './chrome.js';
import { BrandLogo } from './BrandLogo.js';
import { dayPart, firstName } from '../lib/greeting.js';
import { NewMenu } from './NewMenu.js';
import { useMediaQuery, useWindowClass } from './media.js';
import { activeKey, navigationFor } from './nav.js';
import { applyThemeMode, readThemeMode } from './theme.js';

/** The signed in frame: banners, the content, and the bottom bar on a phone or the rail on a wide screen. */
function FrameInner() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The rail from 600 px; the bottom bar below 600.
  const windowClass = useWindowClass();
  const wide = windowClass !== 'compact';
  // The design's rail is the 104 dp one with labels below; labels beside the icons only on a very wide window.
  const expandedRail = useMediaQuery('(min-width: 1200px)');
  const [account, setAccount] = useState(false);
  const [more, setMore] = useState(false);
  const status = useBillingStatus();

  useEffect(() => applyThemeMode(readThemeMode()), []);
  const notices = useQuery({
    queryKey: ['notifications', auth.agencyId, auth.current?.memberId],
    queryFn: () => api<{ id: string; readAt: string | null }[]>('/notifications'),
    enabled: auth.current !== null && auth.allowed('notifications.own'),
    refetchInterval: 60_000,
  });
  const waiting = useQuery({
    queryKey: ['today', auth.agencyId],
    queryFn: () => api<TodayData>('/dashboard/today'),
    enabled: auth.current !== null && auth.allowed('visits.read'),
    refetchInterval: 60_000,
  });
  const nav = useMemo(
    () => (auth.current === null ? null : navigationFor(auth.current.role)),
    [auth.current],
  );

  if (auth.status === 'loading')
    return <p className="k-t-body-lg k-loading-line">{t('app.loading')}</p>;
  if (auth.status === 'anonymous')
    // The root of the app host goes to sign in; any other address comes back after it.
    return (
      <Navigate
        to={
          location.pathname === '/'
            ? '/signin'
            : `/signin?next=${encodeURIComponent(location.pathname)}`
        }
        replace
      />
    );
  if (
    auth.current === null &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/join'
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  if (auth.current === null || nav === null) {
    return (
      <main className="app app--bare">
        <Outlet />
      </main>
    );
  }

  const requested = waiting.data?.visits.requested ?? 0;
  const bar: NavItem[] = nav.tabs.map((d) => ({
    key: d.key,
    ...(d.key === 'visits' && requested > 0 ? { badge: requested } : {}),
    label: t(d.label),
    icon: d.icon,
    ...(d.activeIcon === undefined ? {} : { activeIcon: d.activeIcon }),
    href: d.path,
  }));
  const rail: NavItem[] = [
    ...bar,
    ...nav.more.map((d) => ({ key: d.key, label: t(d.label), icon: d.icon, href: d.path })),
  ];
  if (nav.more.length > 0) bar.push({ key: 'more', label: t('nav.more'), icon: iconMenu });
  const railCurrent = activeKey(location.pathname, [...nav.tabs, ...nav.more]);
  const current = nav.more.some((d) => d.key === railCurrent) ? 'more' : railCurrent;
  const select = (key: string) => {
    if (key === 'more') return setMore(true);
    const dest = [...nav.tabs, ...nav.more].find((d) => d.key === key);
    if (dest !== undefined) void navigate(dest.path);
  };
  const unread = (notices.data ?? []).filter((n) => n.readAt === null).length;
  const chrome = { openAccount: () => setAccount(true), unread, readOnly: isReadOnly(status) };

  return (
    <ChromeContext.Provider value={chrome}>
      <>
        <div className="app">
          <Banners />
          <div className="app__body">
            {wide ? (
              <NavigationRail
                label={t('shell.navigation')}
                items={rail}
                current={railCurrent}
                onSelect={select}
                expanded={expandedRail}
                brand={
                  <span className={expandedRail ? 'rail-brand rail-brand--full' : 'rail-brand'}>
                    <BrandLogo variant={expandedRail ? 'full' : 'mark'} />
                    {expandedRail && firstName(auth.name) !== null ? (
                      <span className="rail-greeting">
                        <span className="k-t-title-sm">
                          {t('shell.hey', { name: `${firstName(auth.name)}!` })}
                        </span>
                        <em className="k-t-body-md k-muted">
                          {t('shell.haveAGreat', { part: t(`shell.part_${dayPart()}`) })}
                        </em>
                      </span>
                    ) : null}
                  </span>
                }
                fab={
                  auth.allowed('properties.write') || auth.allowed('deals.run') ? (
                    <NewMenu team={auth.current.role !== 'agent'} compact labelled={expandedRail} />
                  ) : undefined
                }
              />
            ) : null}
            <main className="app__main">
              <Outlet />
            </main>
          </div>
          {wide ? null : (
            <NavigationBar
              label={t('shell.navigation')}
              items={bar}
              current={current}
              onSelect={select}
            />
          )}
        </div>
        <AccountSheet open={account} onClose={() => setAccount(false)} extras={nav.extras} />
        <BottomSheet
          open={more}
          onClose={() => setMore(false)}
          label={t('nav.more')}
          title={t('nav.more')}
          closeLabel={t('app.close')}
        >
          <List>
            {nav.more.map((d) => (
              <li key={d.key} className="k-list__item">
                <Link className="k-list__row k-layer" to={d.path} onClick={() => setMore(false)}>
                  <span className="k-list__text">
                    <span className="k-t-title-sm">{t(d.label)}</span>
                  </span>
                  <Icon icon={iconChevron} />
                </Link>
              </li>
            ))}
          </List>
        </BottomSheet>
      </>
    </ChromeContext.Provider>
  );
}

/** One snackbar for the whole frame, so a message survives the move from onboarding to Today. */
export function Frame() {
  return (
    <SnackbarProvider>
      <FrameInner />
    </SnackbarProvider>
  );
}
